import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProgressData, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {BLUE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface CitationSuperscriptProps {
  citationId: string;
  citationNumber?: number;
  onCitationClick?: (toolId: string, isSourceCitation?: boolean) => void;
  progressData?: ProgressData;
}

export default function CitationSuperscript({
  citationId,
  citationNumber,
  onCitationClick,
  progressData,
}: CitationSuperscriptProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();

  const handleClick = (): void => {
    if (onCitationClick) {
      onCitationClick(citationId, false);
    }
  };

  const toolCall = progressData?.toolCalls.find((tool) => tool.toolId === citationId);
  const toolDescription = toolCall
    ? getToolDisplayNameFormatted(toolCall.tool, toolCall.args, ToolCallStatus.COMPLETED)
    : 'View citation details';

  const tooltipContent = (
    <div className={css(styles.tooltipContent)}>
      <div>Activity: {toolDescription}</div>
    </div>
  );

  return (
    <sup>
      <Tooltip text={tooltipContent} placement={FloatingPlacement.TOP}>
        <button
          type="button"
          className={css(styles.citationButton)}
          onClick={handleClick}
          {...getTestProps({
            component: 'CitationSuperscript',
            element: 'citationButton',
            key: citationId,
          })}
          data-amp-safe={false}
        >
          [{citationNumber ?? '...'}]
        </button>
      </Tooltip>
    </sup>
  );
}

const styles = StyleSheet.create({
  citationButton: {
    background: 'none',
    border: 'none',
    padding: '0.25rem',
    cursor: 'pointer',
    color: BLUE,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  tooltipContent: {
    fontSize: '12px',
    lineHeight: '1.4',
  },
});
